/**
 * Vista 17 · Auditoría.
 *
 * `SEG-3`: **sin datos personales en el listado.** La acción y la entidad no
 * los contienen por construcción; el motivo sí puede, y por eso el motivo se
 * muestra pero no se indexa ni se usa para filtrar en el servidor.
 *
 * La paginación es del servidor: el registro crece sin tope y traerlo entero
 * para filtrar en el cliente dejaría de funcionar el segundo mes.
 */
import { useState } from 'react'
import { Card, CardContent, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@mpfn/ui'
import {
  DataTable,
  DatePicker,
  FilterBar,
  PaginationControl,
  SearchBar,
  type DataTableColumn,
} from '@mpfn/patterns'
import { useAuditoria, useUsuarios } from '../api/consultas.js'
import type { EventoAuditoria } from '../api/tipos.js'
import { Datos } from '../componentes/Datos.js'
import { Pagina } from '../componentes/Pagina.js'
import { usePuede } from '../sesion/contexto.js'
import { hoy, instante } from '../lib/formato.js'

const TODAS = 'todas'
const TAMANO = 20

/** Las entidades sobre las que se audita. Coinciden con los prefijos de las
 *  acciones que emite el backend. */
const ENTIDADES = [
  'sesion',
  'usuario',
  'rol',
  'permiso',
  'bloque',
  'sorteo',
  'expediente',
  'seleccion',
  'documento_declarado',
  'requerimiento',
  'consulta',
  'respuesta',
  'oci',
  'entidad',
  'configuracion',
  'calendario',
  'circuito',
] as const

/** Cómo se lee cada entidad en el filtro; sin etiqueta, va su código. */
const ETIQUETA_DE_ENTIDAD: Partial<Record<(typeof ENTIDADES)[number], string>> = {
  sorteo: 'selección de muestra',
}

export function Auditoria() {
  // El filtro por usuario solo se ofrece a quien ya puede ver el listado de
  // cuentas: si no, la vista filtraría por identificadores que no puede leer.
  const puedeVerUsuarios = usePuede('usuario.administrar')
  const usuarios = useUsuarios()

  const [entidad, setEntidad] = useState<string>(TODAS)
  const [usuarioId, setUsuarioId] = useState<string>(TODAS)
  const [desde, setDesde] = useState('')
  const [hasta, setHasta] = useState('')
  const [pagina, setPagina] = useState(1)
  const [busqueda, setBusqueda] = useState('')

  const eventos = useAuditoria({
    ...(entidad !== TODAS ? { entidad } : {}),
    ...(usuarioId !== TODAS ? { usuarioId } : {}),
    ...(desde ? { desde } : {}),
    ...(hasta ? { hasta } : {}),
    pagina,
    tamano: TAMANO,
  })

  const columnas: ReadonlyArray<DataTableColumn<EventoAuditoria>> = [
    { id: 'fecha', header: 'Fecha y hora', cell: (fila) => instante(fila.ocurrido_en) },
    { id: 'accion', header: 'Acción', cell: (fila) => <code>{fila.accion}</code> },
    { id: 'entidad', header: 'Entidad', cell: (fila) => fila.entidad, hideOnMobile: true },
    { id: 'usuario', header: 'Responsable', cell: (fila) => fila.usuario },
    { id: 'motivo', header: 'Motivo', cell: (fila) => fila.motivo ?? '—', hideOnMobile: true },
  ]

  function reiniciar(cambio: () => void): void {
    cambio()
    setPagina(1)
  }

  return (
    <Pagina
      titulo="Auditoría"
      descripcion="Quién hizo qué y por qué. El registro no se edita ni se borra."
    >

      <Card>
        <CardContent>
          <Datos estado={eventos}>
            {(datos) => {
              // La búsqueda afina dentro de la página que ya se trajo: el
              // servidor no indexa el motivo, que puede llevar datos personales.
              const texto = busqueda.trim().toLowerCase()
              const filtrados = texto
                ? datos.eventos.filter((fila) =>
                    `${fila.accion} ${fila.motivo ?? ''}`.toLowerCase().includes(texto),
                  )
                : datos.eventos

              return (
                <>
                  <DataTable
                    columns={columnas}
                    rows={filtrados}
                    rowKey={(fila) => fila.id}
                    empty={{ title: 'No hay eventos que coincidan con el filtro' }}
                    toolbar={
                      <FilterBar
                        primary={
                          <SearchBar
                            value={busqueda}
                            onChange={setBusqueda}
                            label="Buscar en esta página"
                            placeholder="Acción o motivo"
                          />
                        }
                        filters={
                          <>
                            <Select
                              value={entidad}
                              onValueChange={(valor) => {
                                reiniciar(() => {
                                  setEntidad(valor)
                                })
                              }}
                            >
                              <SelectTrigger aria-label="Filtrar por entidad">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={TODAS}>Todas las entidades</SelectItem>
                                {ENTIDADES.map((nombre) => (
                                  <SelectItem key={nombre} value={nombre}>
                                    {ETIQUETA_DE_ENTIDAD[nombre] ?? nombre}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                            {puedeVerUsuarios && (
                              <Select
                                value={usuarioId}
                                onValueChange={(valor) => {
                                  reiniciar(() => {
                                    setUsuarioId(valor)
                                  })
                                }}
                              >
                                <SelectTrigger aria-label="Filtrar por responsable">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value={TODAS}>Cualquier responsable</SelectItem>
                                  {(usuarios.data ?? []).map((cuenta) => (
                                    <SelectItem key={cuenta.id} value={cuenta.id}>
                                      {cuenta.nombre}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}

                            <DatePicker
                              id="auditoria-desde"
                              value={desde}
                              placeholder="Desde"
                              onChange={(valor) => {
                                reiniciar(() => {
                                  setDesde(valor)
                                })
                              }}
                              yearRange={{ from: Number(hoy().slice(0, 4)) - 3, to: Number(hoy().slice(0, 4)) }}
                            />

                            <DatePicker
                              id="auditoria-hasta"
                              value={hasta}
                              placeholder="Hasta"
                              onChange={(valor) => {
                                reiniciar(() => {
                                  setHasta(valor)
                                })
                              }}
                              yearRange={{ from: Number(hoy().slice(0, 4)) - 3, to: Number(hoy().slice(0, 4)) }}
                            />
                          </>
                        }
                      />
                    }
                  />

                  <PaginationControl
                    page={datos.pagina}
                    pageSize={datos.tamano}
                    total={datos.total}
                    totalPages={datos.totalPaginas}
                    onPageChange={setPagina}
                  />
                </>
              )
            }}
          </Datos>
        </CardContent>
      </Card>
    </Pagina>
  )
}
